import { useEffect, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import {
  getAssignedPromoters, getAttendanceByMonth,
  getAllAttendanceByMonthForPromoters,
  getPendingAppealsByPromoters, approveAppeal, rejectAppeal, getAllAppeals, getHolidays, getOvertimeByMonth,
} from "../../firebase/services";
import { format, getDaysInMonth } from "date-fns";
import { useLanguage } from "../../hooks/useLanguage";
import { ATT_STATUS, getAttendanceStatus, isPendingLeave, countPaidDays } from "../../firebase/schema";
import { usePayrollConfig } from "../../hooks/usePayrollConfig";
import { AppealHistoryView, APPEAL_STATUS_VI } from "../admin/AdminAttendance";

// ── CSV helpers (reuse admin logic, scoped to assigned promoters) ─────────────
const exportAllToCSV = async (promoters, month, getAttFn) => {
  const allRecords = await getAttFn(month, promoters.map(p => p.uid));
  // Giải trình của tháng → điền vào cột theo đúng ngày
  const monthAppeals = await getAllAppeals(month).catch(() => []);
  const apMap = {};
  monthAppeals.forEach(a => { apMap[`${a.promoterId}_${a.date}`] = a; });
  const apText = (uid, date) => {
    const a = apMap[`${uid}_${date}`];
    if (!a) return "";
    return `${APPEAL_STATUS_VI[a.status]?.label || a.status}: ${a.reason || ""}`;
  };
  const rows = [];
  rows.push(["Promoter", "Cửa hàng", "Ngày", "Check-in", "Check-out", "Tổng giờ", "Ngày lễ", "Ghi chú", "Giải trình"]);
  promoters.forEach(p => {
    const pRecords = allRecords.filter(r => r.promoterId === p.uid).sort((a, b) => a.date.localeCompare(b.date));
    rows.push([]);
    rows.push([`=== ${p.name} – ${p.shopName} ===`]);
    if (pRecords.length === 0) { rows.push([p.name, p.shopName, "(Không có dữ liệu)"]); return; }
    pRecords.forEach(r => rows.push([
      p.name, p.shopName, r.date,
      r.checkinTime || "", r.checkoutTime || "",
      r.totalHours ?? "",
      r.isHoliday ? `Lễ ×${r.holidayMultiplier || 3}` : "",
      r.appealApproved ? "Giải trình OK" : (r.totalHours && r.totalHours < 7 ? "Thiếu giờ" : ""),
      apText(p.uid, r.date),
    ]));
    const daysWorked = pRecords.filter(r => r.checkoutTime).length;
    const holidayDays = pRecords.filter(r => r.isHoliday && r.checkoutTime).length;
    const totalHours = pRecords.reduce((a, r) => a + (r.totalHours || 0), 0);
    rows.push(["", "", "Tổng kết:", `${daysWorked} ngày`, "", `${totalHours.toFixed(1)}h`, `${holidayDays} ngày lễ`]);
  });
  const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = `cham-cong_nhom_${month}.csv`; a.click();
  URL.revokeObjectURL(url);
};

const exportSingleToCSV = (promoterName, month, records) => {
  const rows = records.map(r => [
    r.date, r.checkinTime || "", r.checkoutTime || "",
    r.totalHours ?? "", r.isHoliday ? "Có" : "", r.isHoliday ? (r.holidayMultiplier || 3) : "",
  ]);
  const daysWorked = records.filter(r => r.checkoutTime).length;
  const holidayDays = records.filter(r => r.isHoliday && r.checkoutTime).length;
  const totalHours = records.reduce((a, r) => a + (r.totalHours || 0), 0);
  rows.push([], ["Tổng kết"], ["Ngày đi làm", daysWorked], ["Ngày lễ (×3)", holidayDays], ["Tổng giờ làm", totalHours.toFixed(1) + "h"]);
  const csv = [["Ngày", "Check-in", "Check-out", "Tổng giờ", "Ngày lễ", "Hệ số lễ"], ...rows]
    .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = `cham-cong_${promoterName.replace(/\s+/g, "-")}_${month}.csv`; a.click();
  URL.revokeObjectURL(url);
};

// ── Photo modal ────────────────────────────────────────────
function PhotoModal({ record, onClose }) {
  if (!record) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width: 620, maxWidth: "96vw" }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Ảnh chấm công – {record.date?.slice(8)}/{record.date?.slice(5,7)}/{record.date?.slice(0,4)}</div>
          <button className="btn btn-sm" onClick={onClose}>✕</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {[["Check-in", record.checkinTime, record.checkinPhoto], ["Check-out", record.checkoutTime, record.checkoutPhoto]].map(([label, time, photo]) => (
            <div key={label}>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 6, fontWeight: 500 }}>{label} {time ? `– ${time}` : "–"}</div>
              {photo
                ? <img src={photo} alt={label} style={{ width: "100%", borderRadius: 10, aspectRatio: "4/3", objectFit: "cover", border: "0.5px solid rgba(0,0,0,0.1)" }} />
                : <div style={{ background: "#f5f4f1", borderRadius: 10, aspectRatio: "4/3", display: "flex", alignItems: "center", justifyContent: "center", color: "#bbb", fontSize: 12 }}>Không có ảnh</div>
              }
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          {record.totalHours && <span style={{ fontSize: 12, color: "#555" }}>Tổng: <b>{record.totalHours}h</b></span>}
          {record.isHoliday && <span className="badge badge-amber">Lễ ×{record.holidayMultiplier}</span>}
          {record.totalHours && record.totalHours < 7 && !record.appealApproved && <span className="badge badge-red">Thiếu giờ</span>}
          {record.appealApproved && <span className="badge badge-blue">Đã duyệt giải trình</span>}
        </div>
      </div>
    </div>
  );
}

// ── Overview Tab ───────────────────────────────────────────
function OverviewTab({ month, promoters }) {
  const { t } = useLanguage();
  const [allRecords, setAllRecords] = useState([]);
  const [photoRecord, setPhotoRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthAppeals, setMonthAppeals] = useState([]);
  const [holidayDates, setHolidayDates] = useState([]);
  const [monthOt, setMonthOt] = useState([]);
  const { cfgFor } = usePayrollConfig();
  const work = cfgFor(month).work;

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getAllAttendanceByMonthForPromoters(month, promoters.map(p => p.uid)),
      getAllAppeals(month).catch(() => []),
      getHolidays().catch(() => []),
      getOvertimeByMonth(month).catch(() => []),
    ]).then(([r, ap, hs, ot]) => {
      setAllRecords(r); setMonthAppeals(ap); setHolidayDates(hs.map(h => h.date)); setMonthOt(ot); setLoading(false);
    });
  }, [month, promoters]);

  const appealMap = {};
  monthAppeals.forEach(a => { appealMap[`${a.promoterId}_${a.date}`] = a; });

  const daysInMonth = getDaysInMonth(new Date(month + "-01"));
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const todayStr = format(new Date(), "yyyy-MM-dd");

  const recordMap = {};
  allRecords.forEach(r => {
    if (!recordMap[r.promoterId]) recordMap[r.promoterId] = {};
    recordMap[r.promoterId][r.date] = r;
  });

  const todayRecs = allRecords.filter(r => r.date === todayStr);
  const checkedInToday = todayRecs.filter(r => r.checkinTime).length;
  const doneToday = todayRecs.filter(r => r.checkoutTime).length;
  const notYetToday = promoters.length - checkedInToday;

  // Cùng bộ phân loại với lịch promoter và bảng admin
  const holidaySet = new Set(holidayDates);
  const getCellClass = (rec, dateStr) => {
    if (!rec && dateStr === todayStr) return "";
    const st = getAttendanceStatus({ rec, dateStr, todayStr, isHoliday: holidaySet.has(dateStr), work });
    const cls = ATT_STATUS[st]?.cls || "";
    return isPendingLeave(rec) ? `${cls} att-s-pending` : cls;
  };

  return (
    <>
      {photoRecord && <PhotoModal record={photoRecord} onClose={() => setPhotoRecord(null)} />}
      {month === format(new Date(), "yyyy-MM") && (
        <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
          <div className="metric-card" style={{ flex: "1 1 140px" }}>
            <div className="metric-label">Đã check-in hôm nay</div>
            <div className="metric-value" style={{ color: "#3B6D11" }}>{checkedInToday}</div>
            <div className="metric-sub">/ {promoters.length} promoter</div>
          </div>
          <div className="metric-card" style={{ flex: "1 1 140px" }}>
            <div className="metric-label">Đã hoàn thành ca</div>
            <div className="metric-value">{doneToday}</div>
            <div className="metric-sub">đã check-out</div>
          </div>
          <div className="metric-card" style={{ flex: "1 1 140px" }}>
            <div className="metric-label">Chưa check-in</div>
            <div className="metric-value" style={{ color: notYetToday > 0 ? "#A32D2D" : "#3B6D11" }}>{notYetToday}</div>
            <div className="metric-sub">người vắng mặt</div>
          </div>
        </div>
      )}
      {loading ? <div style={{ color: "#aaa", padding: 20, textAlign: "center" }}>{t("common.loading")}</div> : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div className="table-scroll" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#fafafa" }}>
                  <th style={{ minWidth: 140, padding: "8px 12px", textAlign: "left", position: "sticky", left: 0, background: "#fafafa", borderBottom: "0.5px solid rgba(0,0,0,0.08)", fontWeight: 500, fontSize: 11, color: "#888" }}>Promoter</th>
                  {days.map(d => {
                    const dateStr = `${month}-${String(d).padStart(2, "0")}`;
                    const isToday = dateStr === todayStr;
                    return <th key={d} style={{ width: 28, padding: "6px 2px", textAlign: "center", fontWeight: isToday ? 600 : 400, color: isToday ? "#C8152A" : "#aaa", fontSize: 10, borderBottom: "0.5px solid rgba(0,0,0,0.08)" }}>{d}</th>;
                  })}
                  <th style={{ width: 50, padding: "8px 8px", textAlign: "center", borderBottom: "0.5px solid rgba(0,0,0,0.08)", fontWeight: 500, fontSize: 11, color: "#888" }}>Công</th>
                  <th style={{ width: 60, padding: "8px 8px", textAlign: "center", borderBottom: "0.5px solid rgba(0,0,0,0.08)", fontWeight: 500, fontSize: 11, color: "#888" }}>Giờ</th>
                </tr>
              </thead>
              <tbody>
                {promoters.map(p => {
                  const pMap = recordMap[p.uid] || {};
                  const daysWorked = countPaidDays({
                    records: Object.values(pMap), holidays: holidayDates.map(date => ({ date })),
                    month, startDate: p.startDate, work,
                    overtime: monthOt.filter(o => o.promoterId === p.uid),
                  });
                  const totalHours = Object.values(pMap).reduce((a, r) => a + (r.totalHours || 0), 0);
                  return (
                    <tr key={p.uid} style={{ borderBottom: "0.5px solid rgba(0,0,0,0.05)" }}>
                      <td style={{ position: "sticky", left: 0, background: "#fff", padding: "8px 12px", borderBottom: "0.5px solid rgba(0,0,0,0.05)" }}>
                        <div style={{ fontWeight: 500, fontSize: 12 }}>{p.name}</div>
                        <div style={{ fontSize: 10, color: "#aaa" }}>{p.shopName}</div>
                      </td>
                      {days.map(d => {
                        const dateStr = `${month}-${String(d).padStart(2, "0")}`;
                        const rec = pMap[dateStr];
                        const cls = getCellClass(rec, dateStr);
                        const isToday = dateStr === todayStr;
                        const isFuture = dateStr > todayStr;
                        if (isFuture && !rec) return <td key={d} style={{ padding: "6px 2px" }} />;
                        const ap = appealMap[`${p.uid}_${dateStr}`];
                        const apLabel = ap ? (APPEAL_STATUS_VI[ap.status]?.label || ap.status) : "";
                        const baseTitle = rec ? `${rec.checkinTime || ""}–${rec.checkoutTime || "?"} (${rec.totalHours || "?"}h)` : "Vắng";
                        return (
                          <td key={d} style={{ padding: "6px 2px", textAlign: "center" }}>
                            <div
                              className={`att-matrix-cell ${cls} ${isToday ? "att-matrix-today" : ""}`}
                              onClick={() => rec && setPhotoRecord(rec)}
                              title={baseTitle + (ap ? `\nGiải trình (${apLabel}): ${ap.reason || ""}` : "")}
                              style={{ cursor: rec ? "pointer" : "default", boxShadow: ap ? "inset 0 0 0 2px #7C3AED" : undefined }}
                            />
                          </td>
                        );
                      })}
                      <td style={{ textAlign: "center", fontWeight: 500, padding: "8px 8px" }}>{daysWorked}</td>
                      <td style={{ textAlign: "center", color: "#888", padding: "8px 8px" }}>{totalHours.toFixed(1)}h</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", gap: 14, padding: "10px 14px", borderTop: "0.5px solid rgba(0,0,0,0.06)", fontSize: 11, color: "#888", flexWrap: "wrap" }}>
            {["full", "missing", "short", "holiday", "annualLeave", "unpaidLeave", "weeklyOff"].map(k => (
              <span key={k}><span className={`att-legend ${ATT_STATUS[k].cls}`} /> {t(`att.st_${k}`)}</span>
            ))}
            <span><span className="att-legend" style={{ boxShadow: "inset 0 0 0 2px #7C3AED" }} /> {t("ah.legend_appeal")}</span>
          </div>
        </div>
      )}
    </>
  );
}

// ── Detail Tab ─────────────────────────────────────────────
function DetailTab({ month, setMonth, promoters }) {
  const { t } = useLanguage();
  const [selected, setSelected] = useState("");
  const [records, setRecords] = useState([]);
  const [photoRecord, setPhotoRecord] = useState(null);

  useEffect(() => { if (promoters.length && !selected) setSelected(promoters[0].uid); }, [promoters]);
  useEffect(() => { if (!selected) return; getAttendanceByMonth(selected, month).then(setRecords); }, [selected, month]);

  const selectedPromoter = promoters.find(p => p.uid === selected);
  const recordMap = {}; records.forEach(r => { recordMap[r.date] = r; });
  const daysWorked = records.filter(r => r.checkoutTime).length;
  const holidayDays = records.filter(r => r.isHoliday && r.checkoutTime).length;
  const totalHours = records.reduce((a, r) => a + (r.totalHours || 0), 0);
  const daysInMonth = getDaysInMonth(new Date(month + "-01"));
  const firstDay = new Date(month + "-01").getDay();
  const offset = firstDay === 0 ? 6 : firstDay - 1;
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const todayStr = format(new Date(), "yyyy-MM-dd");

  return (
    <>
      {photoRecord && <PhotoModal record={photoRecord} onClose={() => setPhotoRecord(null)} />}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <select value={selected} onChange={e => setSelected(e.target.value)} style={{ width: 200 }}>
          {promoters.map(p => <option key={p.uid} value={p.uid}>{p.name}</option>)}
        </select>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={{ width: 160 }} />
        <button className="btn" onClick={() => selectedPromoter && exportSingleToCSV(selectedPromoter.name, month, records)} disabled={!records.length}>
          ⬇ Xuất Excel
        </button>
      </div>
      <div className="grid-2">
        <div className="card">
          <div className="card-header">
            <div className="card-title">Lịch tháng {month.slice(5)}/{month.slice(0,4)}</div>
            <div style={{ display: "flex", gap: 6 }}>
              <span className="badge badge-green">● Đủ công</span>
              <span className="badge badge-amber">● Lễ</span>
              <span className="badge badge-gray">● Vắng</span>
            </div>
          </div>
          <div className="att-grid">
            {["T2","T3","T4","T5","T6","T7","CN"].map(d => <div key={d} className="att-day-header">{d}</div>)}
            {Array(offset).fill(null).map((_, i) => <div key={"e"+i} className="att-day att-empty" />)}
            {days.map(d => {
              const dateStr = `${month}-${String(d).padStart(2, "0")}`;
              const rec = recordMap[dateStr];
              const today = dateStr === todayStr;
              let cls = "att-day ";
              if (rec?.checkoutTime) cls += rec.isHoliday ? "att-holiday" : "att-present";
              else if (rec?.checkinTime) cls += "att-present";
              else if (dateStr < todayStr) cls += "att-absent";
              else cls += "att-empty";
              if (today) cls += " att-today";
              return <div key={d} className={cls} title={rec ? `${rec.checkinTime||""}–${rec.checkoutTime||""}` : ""} onClick={() => rec && setPhotoRecord(rec)} style={{ cursor: rec ? "pointer" : "default" }}>{d}</div>;
            })}
          </div>
        </div>
        <div className="card">
          <div className="card-title" style={{ marginBottom: 12 }}>{t("att.summary")}</div>
          <div className="salary-breakdown" style={{ marginBottom: 14 }}>
            <div className="salary-row"><span>{t("att.26_days")}</span><span>26 ngày</span></div>
            <div className="salary-row"><span>{t("att.days_worked")}</span><span>{daysWorked} ngày</span></div>
            <div className="salary-row"><span>{t("att.holiday_days")}</span><span>{holidayDays} ngày</span></div>
            <div className="salary-row"><span>{t("att.total_hours")}</span><span>{totalHours.toFixed(1)}h</span></div>
          </div>
          <div style={{ overflowY: "auto", maxHeight: 280 }}>
            <table><thead><tr><th>Ngày</th><th>{t("att.checkin_time")}</th><th>{t("att.checkout_time")}</th><th>{t("att.hours")}</th><th>{t("common.note")}</th></tr></thead>
              <tbody>
                {records.map(r => (
                  <tr key={r.id} onClick={() => setPhotoRecord(r)} style={{ cursor: "pointer" }}>
                    <td>{r.date.slice(8)}/{r.date.slice(5,7)}</td>
                    <td>{r.checkinTime || "—"}</td>
                    <td>{r.checkoutTime || "—"}</td>
                    <td>{r.totalHours ? r.totalHours + "h" : "—"}</td>
                    <td style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {r.isHoliday && <span className="badge badge-amber">Lễ ×3</span>}
                      {r.totalHours && r.totalHours < 7 && !r.appealApproved && <span className="badge badge-red">Thiếu</span>}
                      {r.appealApproved && <span className="badge badge-blue">Giải trình</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Appeals Tab ────────────────────────────────────────────
function AppealsTab({ user, onCountChange }) {
  const { t } = useLanguage();
  const [mode, setMode] = useState("pending"); // "pending" | "history"
  const [appeals, setAppeals] = useState([]);
  const [rejectNote, setRejectNote] = useState({});
  const [loading, setLoading] = useState(true);
  // History state (lọc theo promoter trong nhóm)
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histMonth, setHistMonth] = useState("");

  const load = async () => {
    setLoading(true);
    const proms = await getAssignedPromoters(user.uid);
    const ids = proms.map(p => p.uid);
    const a = await getPendingAppealsByPromoters(ids);
    setAppeals(a); onCountChange(a.length); setLoading(false);
  };

  useEffect(() => { if (user) load(); }, [user]);

  useEffect(() => {
    if (mode !== "history" || !user) return;
    setHistLoading(true);
    (async () => {
      const proms = await getAssignedPromoters(user.uid);
      const ids = proms.map(p => p.uid);
      const all = await getAllAppeals(histMonth || null);
      setHistory(all.filter(a => ids.includes(a.promoterId)));
      setHistLoading(false);
    })();
  }, [mode, histMonth, user]);

  const ModeSwitch = (
    <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
      <button className={"btn btn-sm" + (mode === "pending" ? " btn-primary" : "")} onClick={() => setMode("pending")}>
        {t("ah.pending_mode")} {appeals.length > 0 ? `(${appeals.length})` : ""}
      </button>
      <button className={"btn btn-sm" + (mode === "history" ? " btn-primary" : "")} onClick={() => setMode("history")}>
        {t("ah.history_mode")}
      </button>
    </div>
  );

  if (mode === "history") return (
    <>
      {ModeSwitch}
      <AppealHistoryView appeals={history} loading={histLoading} filterMonth={histMonth} setFilterMonth={setHistMonth} />
    </>
  );

  if (loading) return <>{ModeSwitch}<div style={{ color: "#aaa", padding: 20, textAlign: "center" }}>{t("common.loading")}</div></>;
  if (!appeals.length) return <>{ModeSwitch}<div className="card" style={{ textAlign: "center", padding: 32, color: "#aaa" }}><div style={{ fontSize: 24, marginBottom: 8 }}>✓</div>{t("att.no_appeals")}</div></>;

  return (
    <>
    {ModeSwitch}
    <div className="card">
      <table className="table-card">
        <thead><tr><th>Ngày</th><th>Promoter</th><th>{t("att.reason")}</th><th>{t("att.submitted_at")}</th><th style={{ width: 260 }}></th></tr></thead>
        <tbody>
          {appeals.map(a => (
            <tr key={a.id}>
              <td data-label="Ngày" style={{ whiteSpace: "nowrap", fontWeight: 500 }}>{a.date?.slice(8)}/{a.date?.slice(5,7)}</td>
              <td data-label="Promoter">{a.promoterName}</td>
              <td data-label="Lý do" style={{ maxWidth: 240 }}><span style={{ fontSize: 12, color: "#555" }}>{a.reason}</span></td>
              <td data-label="Gửi lúc" style={{ fontSize: 11, color: "#aaa", whiteSpace: "nowrap" }}>
                {a.submittedAt?.seconds ? format(new Date(a.submittedAt.seconds * 1000), "dd/MM HH:mm") : "—"}
              </td>
              <td data-label="">
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button className="btn btn-primary btn-sm" onClick={async () => { await approveAppeal(a); load(); }}>✓ {t("common.approve_short")}</button>
                    <button className="btn btn-danger btn-sm" onClick={async () => { await rejectAppeal(a.id, rejectNote[a.id] || ""); load(); }}>✕ {t("common.reject_short")}</button>
                  </div>
                  <input placeholder={t("att.reject_note")} value={rejectNote[a.id] || ""} onChange={e => setRejectNote(n => ({ ...n, [a.id]: e.target.value }))} style={{ fontSize: 12, padding: "4px 8px" }} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </>
  );
}

// ── Main ───────────────────────────────────────────────────
export default function SupervisorAttendance() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [month, setMonth] = useState(format(new Date(), "yyyy-MM"));
  const [promoters, setPromoters] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!user) return;
    getAssignedPromoters(user.uid).then(setPromoters);
  }, [user]);

  const handleExportAll = async () => {
    setExporting(true);
    await exportAllToCSV(promoters, month, getAllAttendanceByMonthForPromoters);
    setExporting(false);
  };

  return (
    <>
      <div className="page-header">
        <div><h1>{t("att.title")}</h1><p>{t("att.subtitle")}</p></div>
        {tab !== "appeals" && (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={{ width: 160 }} />
            <button className="btn btn-primary" onClick={handleExportAll} disabled={exporting || !promoters.length}>
              {exporting ? t("common.exporting") : "⬇ " + t("common.exportAll")}
            </button>
          </div>
        )}
      </div>

      <div className="tabs">
        <div className={`tab ${tab === "overview" ? "active" : ""}`} onClick={() => setTab("overview")}>{t("att.tab_overview")}</div>
        <div className={`tab ${tab === "detail" ? "active" : ""}`} onClick={() => setTab("detail")}>{t("att.tab_detail")}</div>
        <div className={`tab ${tab === "appeals" ? "active" : ""}`} onClick={() => setTab("appeals")}>
          {t("att.tab_appeals_short")} {pendingCount > 0 && <span className="badge badge-red" style={{ marginLeft: 6 }}>{pendingCount}</span>}
        </div>
      </div>

      {tab === "overview" && promoters.length > 0 && <OverviewTab month={month} promoters={promoters} />}
      {tab === "detail" && <DetailTab month={month} setMonth={setMonth} promoters={promoters} />}
      {tab === "appeals" && <AppealsTab user={user} onCountChange={setPendingCount} />}
    </>
  );
}
